import { FC, useEffect } from 'react'
import { Card, Modal } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { reportingTranslations } from 'locale/reporting'
import { showToast } from 'common/utils'
import { CSV_EXPORT_TYPE } from './utils'
import { useGenerateCsv, useGenerateRotaCsv } from './hooks'
import {
  CarePlanExportForm,
  EmarExportForm,
  LogExportForm,
  OutcomesAndGoalsForm,
  RiskAssessmentExportForm,
  RosteringForm,
  ServiceUserExportForm,
  TeamMemberExportForm,
} from './forms'
import { Form } from 'antd'
import { Carer, UserRole } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import saveAs from 'save-as'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { Dict } from 'mixpanel-browser'
import dayjs from 'dayjs'

type Props = {
  isOpen: boolean
  onClose: () => void
  CSVExportType?: CSV_EXPORT_TYPE
  preferredPeopleNominative: string
  preferredCarePlansNominative: string
  formValues?: Record<string, unknown>
}

export const DownloadModal: FC<Props> = ({
  isOpen,
  onClose,
  CSVExportType,
  preferredPeopleNominative,
  preferredCarePlansNominative,
  formValues,
}) => {
  const { t } = useTranslation(NAMESPACE.REPORTING)

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user,
  )

  const isGroupDownload = [UserRole.groupAdmin, UserRole.groupManager].includes(
    user.role,
  )

  const { isLoading: isLoadingDefault, generate: generateDefault } =
    useGenerateCsv(CSVExportType, () => {
      onClose()
      showToast({
        title: `An email with the CSV will be sent to ${user.email}. At peak times this may take a while.`,
      })
    })

  const { isLoading: isLoadingRota, generate: generateRota } =
    useGenerateRotaCsv(() => {
      onClose()
    }, CSVExportType === CSV_EXPORT_TYPE.UPCOMING_ROTA)

  const generate = (
    values: object,
    sendEvent: (event: MixpanelEvents, props: Dict) => void,
  ) => {
    if (
      CSVExportType &&
      [
        CSV_EXPORT_TYPE.ROSTERING_HISTORY,
        CSV_EXPORT_TYPE.UPCOMING_ROTA,
      ]?.includes(CSVExportType)
    ) {
      const typedValues = values as { startDate: string; endDate: string }

      sendEvent(MixpanelEvents.ROSTERING_REPORT_DOWNLOADED, {
        reportType: CSVExportType,
        daysCovered:
          dayjs(typedValues.endDate).diff(dayjs(typedValues.startDate), 'day') +
          1,
      })
      const res = generateRota(values)
        .then((response) => {
          const blob = new Blob([response as unknown as BlobPart], {
            type: 'text/plain;charset=utf-8',
          })
          saveAs(blob, `${CSVExportType}.csv`)
        })
        .catch((err) => {
          console.log(err)
        })

      return res
    }

    return generateDefault(values)
  }

  const isLoading = isLoadingRota || isLoadingDefault

  const [form] = Form.useForm()

  useEffect(() => {
    form.resetFields()
    form.setFieldsValue(formValues)
  }, [formValues, form])

  const getCSVFormType = (CSVExportType?: CSV_EXPORT_TYPE) => {
    switch (CSVExportType) {
      case CSV_EXPORT_TYPE.TEAM_MEMBER:
        return <TeamMemberExportForm isGroupDownload={isGroupDownload} />
      case CSV_EXPORT_TYPE.RESIDENT:
        return (
          <ServiceUserExportForm
            preferredPeopleNominative={preferredPeopleNominative}
            isGroupDownload={isGroupDownload}
          />
        )
      case CSV_EXPORT_TYPE.RISK_ASSESSMENT:
        return (
          <RiskAssessmentExportForm
            preferredPeopleNominative={preferredPeopleNominative}
            preferredCarePlansNominative={preferredCarePlansNominative}
            isGroupDownload={isGroupDownload}
          />
        )
      case CSV_EXPORT_TYPE.CARE_PLAN:
        return (
          <CarePlanExportForm
            preferredPeopleNominative={preferredPeopleNominative}
            preferredCarePlansNominative={preferredCarePlansNominative}
            isGroupDownload={isGroupDownload}
          />
        )
      case CSV_EXPORT_TYPE.LOGS:
        return (
          <LogExportForm
            preferredPeopleNominative={preferredPeopleNominative}
            isGroupDownload={isGroupDownload}
            form={form}
          />
        )
      case CSV_EXPORT_TYPE.EMAR:
        return (
          <EmarExportForm
            preferredPeopleNominative={preferredPeopleNominative}
            isGroupDownload={isGroupDownload}
          />
        )
      case CSV_EXPORT_TYPE.ROSTERING_HISTORY:
        return (
          <RosteringForm
            preferredPeopleNominative={preferredPeopleNominative}
          />
        )
      case CSV_EXPORT_TYPE.UPCOMING_ROTA:
        return (
          <RosteringForm
            preferredPeopleNominative={preferredPeopleNominative}
          />
        )
      case CSV_EXPORT_TYPE.OUTCOME_AND_GOALS:
        return (
          <OutcomesAndGoalsForm
            preferredPeopleNominative={preferredPeopleNominative}
            isGroupDownload={isGroupDownload}
          />
        )
      default:
        return null
    }
  }

  const getModalTitle = () => {
    if (CSVExportType === CSV_EXPORT_TYPE.RESIDENT) {
      return `${t(reportingTranslations.DOWNLOAD, {
        variable: preferredPeopleNominative,
      })}`
    }

    if (CSVExportType === CSV_EXPORT_TYPE.CARE_PLAN) {
      return `${t(reportingTranslations.DOWNLOAD, {
        variable: preferredCarePlansNominative,
      })}`
    }

    if (CSVExportType === CSV_EXPORT_TYPE.ROSTERING_HISTORY) {
      return `${t(reportingTranslations.DOWNLOAD, {
        variable: t(reportingTranslations.ROS_HISTORY_TITLE),
      })}`
    }

    if (CSVExportType === CSV_EXPORT_TYPE.UPCOMING_ROTA) {
      return `${t(reportingTranslations.DOWNLOAD, {
        variable: t(reportingTranslations.UP_ROTA_TITLE),
      })}`
    }

    if (CSVExportType === CSV_EXPORT_TYPE.OUTCOME_AND_GOALS) {
      return `${t(reportingTranslations.DOWNLOAD, {
        variable: t(reportingTranslations.OUTCOME_GOALS_TITLE),
      })}`
    }

    return `${t(reportingTranslations.DOWNLOAD, {
      variable: CSVExportType?.replace('-', ' '),
    })}s`
  }
  const sendEvent = useMixpanelEvent()

  return (
    <Modal
      isModalVisible={isOpen}
      localisation={{
        title: getModalTitle(),
        confirmText: t(reportingTranslations.DOWNLOAD_CSV),
      }}
      onConfirm={form.submit}
      onCancel={() => {
        onClose()
        form.resetFields()
      }}
      confirmButtonProps={{
        variant: 'text',
        isLoading,
      }}
      closeButton
      noPadding
    >
      <Form
        form={form}
        onFinish={async (values: object) => {
          generate(values, sendEvent)
          form.resetFields()
        }}
        layout="vertical"
      >
        <StyledCard>{getCSVFormType(CSVExportType)}</StyledCard>
      </Form>
      <DescText level={2}>{t(reportingTranslations.EMPTY_CSV)}</DescText>
    </Modal>
  )
}

const StyledCard = styled(Card)`
  gap: 12px;
  display: flex;
  flex-direction: column;
  padding: 0px;
`

const DescText = styled(Text)`
  text-align: left;
  color: ${({ theme }) => theme.color.neutral.color5};
`
